import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { apiFetch } from '@/api/client';
import type { Item, ItemVariantAxisOptions } from '@/api/masters';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { controlClass } from '@/components/Field';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { useDebounced } from '@/components/useDebounced';
import type { ReferenceNumberCheck, SerialPreview } from '@core/api/masters';
import { referenceNumbersPath } from '@core/api/queryPaths';
import { toBatchRequest } from '@core/units/intake';
import {
  fillReferences,
  lookName,
  receiveGarments,
  toIntakeDraft,
  type ReceiveDraft,
  type ReceiveGarment,
} from '@core/units/receive';
import {
  referenceLabel,
  referenceProblems,
  referencesToCheck,
  normalizeReference,
} from '@core/units/referenceNumber';

/**
 * Stock-in, step 2 — **each garment** (owner, 2026-10-26, canvas "Garment numbers flow", board
 * B1): a row per garment with its own photo, the code Book in will give it, and a box for the
 * shop's own number (RN). Nothing is saved here; the page books in with these and sends each photo
 * to its own garment afterwards.
 *
 * <p>
 * The codes are the server's preview (`POST /api/item-unit/serial-preview`), counted on across lines
 * as the save counts them. The numbers are checked against every live garment
 * (`GET /api/item-unit/reference-numbers`) so a clash is marked on its row before Book in, never
 * refused at it — and Book in waits while that check is out.
 * </p>
 */
export function ReceiveGarments({
  draft,
  axes,
  designs,
  references,
  onReferences,
  photos,
  onPhotos,
  mayPhotograph,
  busy,
  bookLabel,
  error,
  onBack,
  onBookIn,
}: {
  draft: ReceiveDraft;
  axes: Record<number, ItemVariantAxisOptions[] | undefined>;
  designs: Record<number, Item>;
  /** The typed numbers, keyed by garment. */
  references: Record<string, string>;
  onReferences: (references: Record<string, string>) => void;
  /** Each garment's own photo, keyed by garment. */
  photos: Record<string, File>;
  onPhotos: (photos: Record<string, File>) => void;
  mayPhotograph: boolean;
  busy: boolean;
  bookLabel: string;
  error: unknown;
  onBack: () => void;
  onBookIn: () => void;
}) {
  const garments = useMemo(() => receiveGarments(draft, axes), [draft, axes]);
  const request = useMemo(() => toBatchRequest(toIntakeDraft(draft, axes)), [draft, axes]);

  const preview = useQuery({
    queryKey: ['item-unit', 'serial-preview', request],
    queryFn: () =>
      apiFetch<SerialPreview>('/api/item-unit/serial-preview', { method: 'POST', body: request }),
  });

  const typed = garments.map((garment) => references[garment.key] ?? '');
  const asked = referencesToCheck(typed);
  const settled = useDebounced(asked.join(','));
  const check = useQuery({
    queryKey: ['item-unit', 'reference-numbers', settled],
    queryFn: () =>
      apiFetch<ReferenceNumberCheck>(
        referenceNumbersPath(settled === '' ? [] : settled.split(',')),
      ),
  });

  const problems = referenceProblems(typed, check.data?.taken ?? []);
  const problemCount = problems.filter((problem) => problem !== null).length;
  const checking = settled !== asked.join(',') || check.isFetching;
  const numbered = typed.filter((value) => normalizeReference(value) !== null).length;
  const photographed = garments.filter((garment) => photos[garment.key]).length;
  const next = check.data?.next ?? null;
  const blocked = busy || checking || problemCount > 0 || preview.isLoading;

  const lookOf = (garment: ReceiveGarment) => lookName(garment.look, axes[garment.itemId] ?? []);

  type Row = ReceiveGarment & { index: number };
  const rows: Row[] = garments.map((garment, index) => ({ ...garment, index }));

  const columns: DataViewColumn<Row>[] = [
    {
      key: 'photo',
      title: 'Photo',
      width: 84,
      render: (_, row) =>
        mayPhotograph ? (
          <PhotoPick
            file={photos[row.key]}
            label={`Photo for ${preview.data?.lines[row.lineIndex]?.[row.position] ?? 'this garment'}`}
            disabled={busy}
            onFile={(file) => onPhotos({ ...photos, [row.key]: file })}
          />
        ) : null,
    },
    {
      key: 'code',
      title: 'Our number (RN)',
      primary: true,
      render: (_, row) => {
        const code = preview.data?.lines[row.lineIndex]?.[row.position];
        const reference = normalizeReference(references[row.key]);

        return (
          <span className="font-mono-nums text-[15px] font-bold whitespace-nowrap text-gray-900">
            {code ?? (preview.isError ? '—' : '…')}
            {reference && (
              <span className="text-default ms-1 font-semibold">({referenceLabel(reference)})</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'garment',
      title: 'Garment',
      render: (_, row) => (
        <span className="flex min-w-0 flex-col">
          <span className="text-xs font-semibold text-gray-900">
            {designs[row.itemId]?.name ?? 'Design'} · {row.sizeLabel}
          </span>
          <span className="text-default text-[11px]">{lookOf(row) || ' '}</span>
        </span>
      ),
    },
    {
      key: 'reference',
      title: 'RN · reference number (optional)',
      width: 220,
      render: (_, row) => (
        <span className="flex flex-col gap-0.5">
          <input
            aria-label={`Reference number for garment ${row.index + 1}`}
            value={references[row.key] ?? ''}
            disabled={busy}
            placeholder="—"
            onChange={(event) => onReferences({ ...references, [row.key]: event.target.value })}
            className={`${controlClass} font-semibold ${
              problems[row.index] ? 'border-danger' : ''
            }`}
          />
          {problems[row.index] && (
            <span className="text-danger text-[11px] font-semibold">{problems[row.index]}</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3 lg:gap-4">
      <PageHeading
        eyebrow="New stock · step 2 of 2"
        title={`The ${garments.length} garment${garments.length === 1 ? '' : 's'}, one by one`}
        subtitle="A photo each, and your own number if the garment has one."
        overlap
        actions={
          <>
            <Button onClick={onBack} disabled={busy} shortcut="back">
              Back
            </Button>
            <Button
              variant="primary"
              icon="icon icon-check"
              shortcut="save"
              data-enter-key-commit
              disabled={blocked}
              onClick={onBookIn}
            >
              {bookLabel}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-12 gap-3 lg:gap-4">
        <div className="col-span-12 xl:col-span-8">
          <Card>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="mb-0 text-sm font-semibold text-gray-900">Each garment</p>
              <div className="flex flex-wrap gap-2">
                {next !== null && (
                  <Button
                    disabled={busy}
                    onClick={() => onReferences(fillReferences(garments, next))}
                  >
                    {`Fill their numbers from ${next} ↓`}
                  </Button>
                )}
                <Button disabled={busy || numbered === 0} onClick={() => onReferences({})}>
                  Clear
                </Button>
              </div>
            </div>
            {preview.isError && (
              <Notice tone="warning" title="The codes could not be read">
                They are still issued when the stock is booked in.
              </Notice>
            )}
            <DataView columns={columns} dataSource={rows} rowKey="key" />
            <p className="text-default mt-2 mb-0 text-[length:var(--scale-list-meta)]">
              Our number is shown as it will be issued; if another counter books the same design and
              size first, it moves on by one and the tags print the final one. Your number is kept
              as typed.
            </p>
          </Card>
        </div>

        <div className="col-span-12 flex flex-col gap-3 lg:gap-4 xl:col-span-4">
          <Card className="xl:sticky xl:top-24">
            <p className="text-sm font-semibold text-gray-900">Ready to book in</p>
            <ul className="mt-2 flex flex-col gap-1 ps-0 text-[length:var(--scale-list-meta)]">
              <SummaryRow label="Garments" value={String(garments.length)} />
              {mayPhotograph && (
                <SummaryRow label="With a photo" value={`${photographed} of ${garments.length}`} />
              )}
              <SummaryRow label="With your number" value={`${numbered} of ${garments.length}`} />
            </ul>
            {problemCount > 0 && (
              <p className="text-danger mt-2 mb-0 text-[length:var(--scale-list-meta)]">
                {problemCount === 1
                  ? 'One number needs changing before booking in.'
                  : `${problemCount} numbers need changing before booking in.`}
              </p>
            )}
            {error != null && (
              <p className="text-danger mt-2 mb-0 text-[length:var(--scale-list-meta)]">
                {error instanceof Error ? error.message : 'It did not book in. Try again.'}
              </p>
            )}
            <div className="mt-3">
              <Button
                variant="primary"
                icon="icon icon-check"
                className="w-full justify-center"
                disabled={blocked}
                onClick={onBookIn}
              >
                {bookLabel}
              </Button>
            </div>
            <p className="text-default mt-2 mb-0 text-[length:var(--scale-list-meta)]">
              Nothing is saved until Book in. The photos go up straight after, each to its own
              garment; one without a photo shows the design's.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <li className="text-default flex list-none justify-between gap-2">
      <span>{label}</span>
      <span className="font-semibold text-gray-900">{value}</span>
    </li>
  );
}

/** A garment's own photo, held here until Book in sends it. Tapping a taken one changes it. */
function PhotoPick({
  file,
  label,
  disabled,
  onFile,
}: {
  file: File | undefined;
  label: string;
  disabled: boolean;
  onFile: (file: File) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setUrl(null);

      return undefined;
    }

    const made = URL.createObjectURL(file);
    setUrl(made);

    return () => URL.revokeObjectURL(made);
  }, [file]);

  return (
    <label
      className={`border-border-color hover:bg-light flex h-16 w-14 cursor-pointer items-center justify-center overflow-hidden rounded-md border border-dashed bg-white text-[11px] font-semibold text-gray-900 ${
        disabled ? 'pointer-events-none opacity-50' : ''
      }`}
    >
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : '+ Photo'}
      <input
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        aria-label={label}
        onChange={(event) => {
          const picked = event.target.files?.[0];
          event.target.value = '';

          if (picked) {
            onFile(picked);
          }
        }}
      />
    </label>
  );
}
